import { type FC, useState } from "react";
import { ButtonUI } from "../../../../../components/Button";
import {
  type User,
  useAssignUserToOrganizationRestApi,
  useUnassignedUsersRestApi,
} from "../../../../../hooks/useOrganizationsRestApi";

interface AssignUserModalProps {
  isOpen: boolean;
  organizationId: string;
  organizationName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const AssignUserModal: FC<AssignUserModalProps> = ({
  isOpen,
  organizationId,
  organizationName,
  onClose,
  onSuccess,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isAssigning, setIsAssigning] = useState(false);

  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useUnassignedUsersRestApi({
    skip: !isOpen,
  });

  const { assignUserToOrganization } = useAssignUserToOrganizationRestApi();

  const unassignedUsers: User[] = usersData?.unassignedUsers || [];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(unassignedUsers.map(user => user.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleAssignUsers = async () => {
    if (selectedUsers.size === 0) return;

    setIsAssigning(true);
    try {
      // 選択されたユーザーを順次割り当て
      for (const userId of selectedUsers) {
        await assignUserToOrganization({
          userId: userId,
          organizationId,
        });
      }

      onSuccess();
      onClose();
      setSelectedUsers(new Set());
    } catch (_err) {
      alert("ユーザーの割り当てに失敗しました");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCancel = () => {
    setSelectedUsers(new Set());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            ユーザーを「{organizationName}」に追加
          </h3>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mt-4">
          {usersLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">読み込み中...</span>
            </div>
          ) : usersError ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-2">エラーが発生しました</div>
              <p className="text-gray-600">{usersError.message}</p>
            </div>
          ) : unassignedUsers.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">👤</div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                割り当て可能なユーザーがいません
              </h4>
              <p className="text-gray-600">
                すべてのユーザーが既に組織に所属しています
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  組織に所属していないユーザーの一覧です。選択したユーザーを「
                  {organizationName}」に追加できます。
                </p>
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={
                        selectedUsers.size === unassignedUsers.length &&
                        unassignedUsers.length > 0
                      }
                      onChange={e => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      すべて選択
                    </span>
                  </label>
                  {selectedUsers.size > 0 && (
                    <span className="text-sm text-blue-600">
                      {selectedUsers.size}人選択中
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-500">
                  {unassignedUsers.length}人の未割り当てユーザー
                </span>
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        選択
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ユーザー名
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        メールアドレス
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {unassignedUsers.map(user => (
                      <tr
                        key={user.id}
                        className={`hover:bg-gray-50 ${
                          selectedUsers.has(user.id) ? "bg-blue-50" : ""
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={e =>
                              handleSelectUser(user.id, e.target.checked)
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                                {(user.name ?? "").charAt(0)}
                              </div>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name ?? ""}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.kana || ""}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {user.id}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <ButtonUI
            onClick={handleCancel}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700"
            disabled={isAssigning}
          >
            キャンセル
          </ButtonUI>
          <ButtonUI
            onClick={handleAssignUsers}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={
              selectedUsers.size === 0 ||
              isAssigning ||
              unassignedUsers.length === 0
            }
          >
            {isAssigning ? (
              <>
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                割り当て中...
              </>
            ) : (
              `${selectedUsers.size}人を追加`
            )}
          </ButtonUI>
        </div>
      </div>
    </div>
  );
};
